import { UserStarBalance } from "@/src/models/rewardly.model";
import { useTranslation } from "react-i18next";
import { Text, useWindowDimensions, View } from "react-native";
import { DataTable } from "react-native-paper";
import StateDisplay from "../../ui-kit/state-display";
import SnapshotAvatar from "../snapshot-avatar";

export type Balance = UserStarBalance & {
  starsReceived: number;
  monetaryValue: string;
};

type BalancesTableProps = Readonly<{
  balances: Balance[];
}>;

const EmptyState = () => (
  <StateDisplay
    descriptionLabel="LABEL_NO_BALANCES_FOUND"
    imgSource={require("@/src/assets/images/empty.png")}
  />
);

/** Stacked card per user — the table layout doesn't fit phone widths. */
const BalanceCard = ({ balance }: { balance: Balance }) => {
  const { t } = useTranslation();
  return (
    <View className="flex-col gap-3 p-4 bg-chalk rounded-xl md:border md:border-ash-3">
      <View className="flex-row items-center gap-3">
        <SnapshotAvatar user={balance.userInfo} size={36} />
        <View className="flex-1">
          <Text className="text-onyx font-medium">{balance.userInfo?.displayName}</Text>
          <Text className="text-xs text-ash-2">{balance.userInfo?.email}</Text>
        </View>
      </View>
      <View className="flex-row border-t border-ash-3 pt-3">
        <View className="flex-1 items-center gap-0.5">
          <Text className="text-xs text-ash-2">{t("LABEL_RECEIVED_STARS")}</Text>
          <Text className="text-primary font-semibold">{balance.starsReceived}</Text>
        </View>
        <View className="flex-1 items-center gap-0.5 border-x border-ash-3">
          <Text className="text-xs text-ash-2">{t("LABEL_MONETARY_VALUE")}</Text>
          <Text className="text-jade font-semibold">{balance.monetaryValue}</Text>
        </View>
        <View className="flex-1 items-center gap-0.5">
          <Text className="text-xs text-ash-2">{t("LABEL_ALLOWANCE_LEFT")}</Text>
          <Text className="text-onyx font-semibold">{balance.starsRemaining}</Text>
        </View>
      </View>
    </View>
  );
};

const BalancesTable = ({ balances }: BalancesTableProps) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  if (balances.length === 0) {
    return <EmptyState />;
  }

  if (isMobile) {
    return (
      <View className="flex-col gap-3">
        {balances.map((balance) => (
          <BalanceCard key={balance.itemId ?? balance.ItemId} balance={balance} />
        ))}
      </View>
    );
  }

  return (
    <View>
      <DataTable.Header style={{ borderBottomWidth: 1, borderBottomColor: "var(--color-ash-3)", elevation: 0 }}>
        <DataTable.Title style={{ flex: 2 }}>
          <Text className="text-xs text-onyx font-medium tracking-wide">{t("LABEL_NAME")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 2 }}>
          <Text className="text-xs text-onyx font-medium tracking-wide">{t("LABEL_EMAIL")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1, justifyContent: "center" }}>
          <Text className="text-xs text-onyx font-medium tracking-wide text-center">{t("LABEL_RECEIVED_STARS")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1, justifyContent: "center" }}>
          <Text className="text-xs text-onyx font-medium tracking-wide text-center">{t("LABEL_MONETARY_VALUE")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1, justifyContent: "center" }}>
          <Text className="text-xs text-onyx font-medium tracking-wide text-center">{t("LABEL_ALLOWANCE_LEFT")}</Text>
        </DataTable.Title>
      </DataTable.Header>

      {balances.map((item) => (
        <DataTable.Row
          key={item.itemId ?? item.ItemId}
          style={{ borderBottomWidth: 1, borderBottomColor: "var(--color-ash-3)", paddingVertical: 10 }}
        >
          <DataTable.Cell style={{ flex: 2 }}>
            <View className="flex-row items-center gap-2">
              <SnapshotAvatar user={item.userInfo} size={32} />
              <Text className="text-onyx">{item.userInfo?.displayName}</Text>
            </View>
          </DataTable.Cell>
          <DataTable.Cell style={{ flex: 2 }}>
            <Text className="text-ash-2">{item.userInfo?.email}</Text>
          </DataTable.Cell>
          <DataTable.Cell style={{ flex: 1, justifyContent: "center" }}>
            <Text className="text-primary font-semibold text-center">{item.starsReceived}</Text>
          </DataTable.Cell>
          <DataTable.Cell style={{ flex: 1, justifyContent: "center" }}>
            <Text className="text-jade font-semibold text-center">{item.monetaryValue}</Text>
          </DataTable.Cell>
          <DataTable.Cell style={{ flex: 1, justifyContent: "center" }}>
            <Text className="text-onyx font-medium text-center">{item.starsRemaining}</Text>
          </DataTable.Cell>
        </DataTable.Row>
      ))}
    </View>
  );
};

export default BalancesTable;
